import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ChevronRight, 
  ChevronDown, 
  Folder, 
  FolderOpen, 
  Layers, 
  LayoutDashboard, 
  Workflow, 
  Check,
  Star,
  StarOff,
  Sun,
  Moon,
  Monitor,
  Palette
} from 'lucide-react';
import { ThemePreset } from '../../theme/presets';
import { useMenu } from '../../hooks/useMenu';
import { useAuth } from '../../contexts/AuthContext';
import { useSignature } from '../../contexts/SignatureContext';
import { Flavor, ModePreference } from '../../theme/signatureTokens';
import { TelaResponse } from '../../types/api';

type TelaComChildren = TelaResponse & { children?: TelaComChildren[] };

/** Sabores oficiais do Galatea Signature (recursos de aparência). */
const APPEARANCE_FLAVORS: { id: Flavor; label: string; desc: string }[] = [
  { id: 'SIGNATURE', label: 'Signature', desc: 'Padrão corporativo' },
  { id: 'EXECUTIVE', label: 'Executive', desc: 'Alta profundidade & KPIs' },
  { id: 'PROCESS', label: 'Process', desc: 'Orientado a processos' },
  { id: 'MINIMAL', label: 'Minimal', desc: 'Alta densidade operacional' },
];

/** Luminosidade: 'auto' segue a preferência do sistema operacional. */
const APPEARANCE_MODES: { id: ModePreference; label: string; Icon: typeof Sun }[] = [
  { id: 'light', label: 'Claro', Icon: Sun },
  { id: 'dark', label: 'Escuro', Icon: Moon },
  { id: 'auto', label: 'Auto', Icon: Monitor },
];

interface SidebarProps {
  open?: boolean;
  currentPalette?: ThemePreset;
}

export function Sidebar(_props?: SidebarProps) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [appearanceOpen, setAppearanceOpen] = useState<boolean>(true);
  const [favorites, setFavorites] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { colors, flavor, modePref, setFlavor, setModePreference } = useSignature();

  const { getEmpresaId, isSuperAdminMode, getUserContext } = useAuth();
  const empresaId = getEmpresaId();
  // Áreas Funcionais: tela administrativa do tenant (níveis 0, 1 e 2)
  const nivelAcesso = getUserContext()?.nivel_acesso_efetivo;
  const mostrarAreasFuncionais = nivelAcesso != null && nivelAcesso <= 2;
  const { menuItems, isLoading } = useMenu(
    'GALATEA_WEB',
    !isSuperAdminMode() ? empresaId : undefined
  );

  useEffect(() => {
    try {
      const raw = localStorage.getItem('galatea_favorites_menu');
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) setFavorites(arr.filter((p) => typeof p === 'string'));
      }
    } catch {}
  }, []);

  const persistFavorites = (arr: string[]) => {
    setFavorites(arr);
    try {
      localStorage.setItem('galatea_favorites_menu', JSON.stringify(arr));
    } catch {}
  };

  const toggleFavorite = (path: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!path) return;
    if (favorites.includes(path)) {
      persistFavorites(favorites.filter((p) => p !== path));
    } else {
      persistFavorites([...favorites, path]);
    }
  };

  useEffect(() => {
    const flatten = (items: TelaComChildren[]): TelaComChildren[] =>
      items.flatMap(i => [i, ...flatten((i.children ?? []) as TelaComChildren[])]);

    const allFlat = flatten(menuItems as TelaComChildren[]);
    const current = allFlat.find(i => i.rota === location.pathname);
    if (!current) return;

    const toExpand = new Set<number>();
    const findAncestors = (items: TelaComChildren[], targetId: number): boolean => {
      for (const item of items) {
        if (item.id === targetId) return true;
        if (item.children?.length) {
          if (findAncestors(item.children as TelaComChildren[], targetId)) {
            toExpand.add(item.id);
            return true;
          }
        }
      }
      return false;
    };

    findAncestors(menuItems as TelaComChildren[], current.id);
    setExpandedIds(prev => new Set([...prev, ...toExpand]));
  }, [location.pathname, menuItems]);

  const toggleExpand = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleItemClick = (item: TelaComChildren) => {
    if (item.children && item.children.length > 0) {
      setExpandedIds(prev => {
        const next = new Set(prev);
        if (next.has(item.id)) next.delete(item.id);
        else next.add(item.id);
        return next;
      });
    }
    if (item.rota) {
      navigate(item.rota);
    }
  };

  const rootItems = (menuItems as TelaComChildren[]).filter(
    i => i.parent_id === null || i.parent_id === undefined
  );

  const renderItem = (item: TelaComChildren, depth: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedIds.has(item.id);
    const isActive = location.pathname === item.rota;
    const isFavorite = item.rota ? favorites.includes(item.rota) : false;

    return (
      <div key={item.id} className="select-none">
        <div
          onClick={() => handleItemClick(item)}
          className="group flex items-center justify-between px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer"
          style={{
            paddingLeft: `${12 + depth * 14}px`,
            backgroundColor: isActive ? colors.sidebarSelected : 'transparent',
            color: isActive ? '#FFFFFF' : '#94A3B8',
          }}
        >
          <div className="flex items-center gap-2 min-w-0">
            {hasChildren ? (
              isExpanded ? (
                <FolderOpen className="w-3.5 h-3.5 text-sky-400 shrink-0" />
              ) : (
                <Folder className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              )
            ) : (
              <Layers className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            )}
            <span className="truncate">{item.nome}</span>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {!hasChildren && item.rota && (
              <button
                onClick={e => toggleFavorite(item.rota, e)}
                className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-slate-700/50 transition-opacity"
                style={{ opacity: isFavorite ? 1 : undefined }}
              >
                {isFavorite ? (
                  <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                ) : (
                  <StarOff className="w-3.5 h-3.5 text-slate-400" />
                )}
              </button>
            )}
            {hasChildren && (
              <button
                onClick={e => toggleExpand(item.id, e)}
                className="p-0.5 rounded hover:bg-slate-700/50 text-slate-400"
              >
                {isExpanded ? (
                  <ChevronDown className="w-3.5 h-3.5" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5" />
                )}
              </button>
            )}
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="space-y-0.5 mt-0.5">
            {item.children!.map(child => renderItem(child as TelaComChildren, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className="w-64 flex flex-col overflow-hidden shrink-0 border-r select-none transition-colors duration-200"
      style={{
        backgroundColor: colors.bgSidebar,
        borderColor: colors.border,
        color: '#F1F5F9',
      }}
    >
      <div className="flex-1 overflow-y-auto py-3 px-2 mt-1 space-y-1 scrollbar-standard">
        {/* Aparência (Galatea Signature) — recurso oficial, persistido por usuário + empresa */}
        <div
          className="mb-3 p-2 rounded-xl border"
          style={{ backgroundColor: colors.bgSurface, borderColor: colors.border }}
        >
          <div
            onClick={() => setAppearanceOpen(!appearanceOpen)}
            className="flex items-center justify-between text-[10px] font-black uppercase tracking-wider cursor-pointer select-none"
            style={{ color: colors.textSecondary }}
          >
            <div className="flex items-center gap-1.5">
              <Palette className="w-3.5 h-3.5" style={{ color: colors.primary }} />
              <span>Aparência</span>
            </div>
            <div className="flex items-center gap-1">
              <span
                className="text-[9px] px-1.5 py-0.2 rounded font-mono"
                style={{ backgroundColor: colors.primaryLight, color: colors.primary }}
              >
                {flavor}
              </span>
              {appearanceOpen ? (
                <ChevronDown className="w-3 h-3" style={{ color: colors.textSecondary }} />
              ) : (
                <ChevronRight className="w-3 h-3" style={{ color: colors.textSecondary }} />
              )}
            </div>
          </div>

          {appearanceOpen && (
            <div className="space-y-1 mt-2">
              {APPEARANCE_FLAVORS.map((item) => {
                const isActive = flavor === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setFlavor(item.id)}
                    title={item.desc}
                    className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer"
                    style={{
                      backgroundColor: isActive ? colors.primary : 'transparent',
                      color: isActive ? '#FFFFFF' : colors.textSecondary,
                    }}
                  >
                    <span>{item.label}</span>
                    {isActive ? (
                      <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded font-mono uppercase tracking-wider flex items-center gap-1">
                        <Check className="w-3 h-3" /> Ativo
                      </span>
                    ) : (
                      <span className="text-[9px] font-mono opacity-70">Selecionar</span>
                    )}
                  </button>
                );
              })}
              {/* Luminosidade: Claro / Escuro / Auto (segue o sistema operacional) */}
              <div
                className="flex items-center gap-1 mt-2 pt-2"
                style={{ borderTop: `1px solid ${colors.border}` }}
              >
                {APPEARANCE_MODES.map(({ id, label, Icon }) => {
                  const isActive = modePref === id;
                  return (
                    <button
                      key={id}
                      onClick={() => setModePreference(id)}
                      title={id === 'auto' ? 'Segue a preferência do sistema operacional' : `Modo ${label}`}
                      className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer"
                      style={{
                        backgroundColor: isActive ? colors.primary : 'transparent',
                        color: isActive ? '#FFFFFF' : colors.textSecondary,
                      }}
                    >
                      <Icon className="w-3.5 h-3.5" /> {label}
                    </button>
                  );
                })}
              </div>

              <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${colors.border}` }}>
                <button
                  onClick={() => navigate('/aparencia')}
                  className="w-full text-center py-1.5 text-[10px] uppercase font-bold tracking-wider rounded transition-colors cursor-pointer"
                  style={{ color: colors.primary }}
                >
                  Avançado (Aparência)
                </button>
                <p className="text-[9px] text-center mt-1" style={{ color: colors.textMuted }}>
                  Aplicado automaticamente ao seu usuário
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-2.5 opacity-80">
          MENU PRINCIPAL
        </div>

        {/* 1. Item Mestre: Fluxos */}
        <button
          onClick={() => navigate('/fluxos')}
          className="w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer"
          style={{
            backgroundColor: location.pathname === '/fluxos' ? colors.sidebarSelected : 'transparent',
            color: location.pathname === '/fluxos' ? '#FFFFFF' : '#94A3B8',
          }}
          title="Fluxos de Trabalho e Processos"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Workflow className="w-3.5 h-3.5 text-slate-300 shrink-0" />
            <span className="truncate">Fluxos</span>
          </div>
          <span className="text-[9px] font-extrabold uppercase tracking-wide bg-sky-600/80 text-white px-1.5 py-0.5 rounded">
            novo
          </span>
        </button>

        {/* 2. Item Mestre: Painel de Controle */}
        <button
          onClick={() => navigate('/dashboard')}
          className="w-full flex items-center gap-2 px-3 py-1.5 mb-2 rounded-lg text-xs font-semibold transition-all cursor-pointer"
          style={{
            backgroundColor: location.pathname === '/' || location.pathname === '/dashboard' ? colors.sidebarSelected : 'transparent',
            color: location.pathname === '/' || location.pathname === '/dashboard' ? '#FFFFFF' : '#94A3B8',
          }}
          title="Painel de Controle e Visão Geral"
        >
          <LayoutDashboard className="w-3.5 h-3.5 text-slate-300 shrink-0" />
          <span className="truncate">Painel de Controle</span>
        </button>

        {/* 3. Item fixo: Áreas Funcionais — operação do tenant (o backend escopa pelo vínculo) */}
        {mostrarAreasFuncionais && (
          <button
            onClick={() => navigate('/areas-funcionais')}
            className="w-full flex items-center justify-between px-3 py-1.5 mb-2 rounded-lg text-xs font-semibold transition-all cursor-pointer"
            style={{
              backgroundColor: location.pathname === '/areas-funcionais' ? colors.sidebarSelected : 'transparent',
              color: location.pathname === '/areas-funcionais' ? '#FFFFFF' : '#94A3B8',
            }}
            title="Áreas Funcionais do grupo de empresas"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Layers className="w-3.5 h-3.5 text-slate-300 shrink-0" />
              <span className="truncate">Áreas Funcionais</span>
            </div>
            <span className="text-[9px] font-extrabold uppercase tracking-wide bg-sky-600/80 text-white px-1.5 py-0.5 rounded">
              adm
            </span>
          </button>
        )}

        <div className="my-2 border-t" style={{ borderColor: colors.border }} />

        {favorites.length > 0 && (
          <div className="mb-2">
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 px-2.5 opacity-80 flex items-center gap-1.5">
              <Star className="w-3 h-3" /> FAVORITOS
            </div>
            {favorites.map(favPath => {
              const flatten = (items: TelaComChildren[]): TelaComChildren[] =>
                items.flatMap(i => [i, ...flatten((i.children ?? []) as TelaComChildren[])]);
              const allFlat = flatten(menuItems as TelaComChildren[]);
              const favItem = allFlat.find(i => i.rota === favPath);
              if (!favItem) return null;
              
              const isFavActive = location.pathname === favPath;
              return (
                <button
                  key={favPath}
                  onClick={() => navigate(favPath)}
                  className="w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer group"
                  style={{
                    backgroundColor: isFavActive ? colors.sidebarSelected : 'transparent',
                    color: isFavActive ? '#FFFFFF' : '#94A3B8',
                  }}
                  title={favItem.nome}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Star className="w-3 h-3 text-amber-400 fill-amber-400 shrink-0" />
                    <span className="truncate">{favItem.nome}</span>
                  </div>
                </button>
              );
            })}
            <div className="my-2 border-t" style={{ borderColor: colors.border }} />
          </div>
        )}

        {isLoading ? (
          <div className="px-3 text-xs text-slate-400 animate-pulse">Carregando...</div>
        ) : rootItems.length > 0 ? (
          rootItems.map(item => renderItem(item, 0))
        ) : (
          <div className="px-3 text-xs text-slate-400">Nenhum menu disponível</div>
        )}
      </div>

      {/* Badge Técnico de Build galatea-web */}
      <div
        className="p-2 border-t text-[10px] text-slate-400/80 font-mono tracking-tight text-center select-all shrink-0"
        style={{
          backgroundColor: colors.bgHeader,
          borderColor: colors.border,
        }}
      >
        GalateaIA • build 2026.08.26.1740 • commit 51ebf18
      </div>
    </aside>
  );
}
